import React from "react";
import {
  View,
  Text,
  StyleSheet,
} from "@react-pdf/renderer";
import moment from "moment";

// --- Color Palette ---
const colors = {
  primaryBlue: "#123460",
  lightGrayBg: "#F9FAFB",
  borderColor: "#D1D5DB",
  textLabel: "#4B5563",
  textValue: "#111827",
};

// --- Styles ---
const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 8,
    fontFamily: "Helvetica",
    backgroundColor: "#FFFFFF",
  },
  pdfSaveId: {
    fontSize: 5,
    color: "#fff",
    textAlign: "right",
    marginBottom: 5,
  },

  // -- Header --
  mainTitleContainer: {
    marginBottom: 10,
    borderBottomWidth: 1.5,
    borderBottomColor: colors.primaryBlue,
    paddingBottom: 6,
  },
  mainTitle: {
    fontSize: 14,
    color: colors.primaryBlue,
    fontFamily: "Helvetica-Bold",
    textTransform: "uppercase",
  },
  subTitleRow: {
    flexDirection: "row",
    marginTop: 4,
    color: colors.textValue,
  },

  // -- Section Headers --
  sectionHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 12,
    marginBottom: 6,
  },
  sectionTitleText: {
    fontSize: 9,
    color: colors.primaryBlue,
    fontFamily: "Helvetica-Bold",
    textTransform: "uppercase",
  },
  sectionLine: {
    flex: 1,
    height: 1,
    backgroundColor: colors.borderColor,
    marginLeft: 8,
  },

  // -- Key-Value Pairs Layout (Compact Grid) --
  kvContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderColor: colors.borderColor,
  },
  kvItem: {
    width: "50%",
    flexDirection: "row",
    borderBottomWidth: 1,
    borderRightWidth: 1,
    borderColor: colors.borderColor,
    alignItems: "stretch",
  },
  kvLabel: {
    width: "40%",
    backgroundColor: colors.lightGrayBg,
    paddingVertical: 3,
    paddingHorizontal: 4,
    color: colors.textLabel,
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
    borderRightWidth: 1,
    borderRightColor: colors.borderColor,
  },
  kvValue: {
    width: "60%",
    paddingVertical: 3,
    paddingHorizontal: 4,
    color: colors.textValue,
    fontSize: 7,
  },

  // -- Events Table --
  table: {
    marginTop: 2,
    borderWidth: 1,
    borderColor: colors.borderColor,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: colors.primaryBlue,
  },
  tableHeaderCell: {
    padding: 4,
    fontSize: 7,
    color: "#FFFFFF",
    fontFamily: "Helvetica-Bold",
    borderRightWidth: 1,
    borderRightColor: "#2A4B7C",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: colors.borderColor,
  },
  tableCell: {
    padding: 4,
    fontSize: 7,
    color: colors.textValue,
    borderRightWidth: 1,
    borderRightColor: colors.borderColor,
  },

  // -- Remarks (Vertical List) --
  remarkBlock: {
    marginBottom: 6,
    borderWidth: 1,
    borderColor: colors.borderColor,
  },
  remarkHeader: {
    backgroundColor: colors.lightGrayBg,
    paddingVertical: 3,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderColor,
    color: colors.primaryBlue,
    fontFamily: "Helvetica-Bold",
    fontSize: 8,
  },
  remarkBody: {
    padding: 4,
  },
  remarkLine: {
    fontSize: 7,
    color: colors.textValue,
    marginBottom: 2,
  },

  // -- Authors --
  authorGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  authorCard: {
    width: "31%",
    padding: 6,
    borderWidth: 1,
    borderColor: colors.borderColor,
    backgroundColor: colors.lightGrayBg,
  }
});

const SectionTitle = ({ title }) => (
  <View style={styles.sectionHeaderRow} wrap={false}>
    <Text style={styles.sectionTitleText}>{title}</Text>
    <View style={styles.sectionLine} />
  </View>
);

const StatementOfFactsPdf = ({ state, pdfSaveId }) => {
  const { vesselData = {}, userFields = [], events = [], remarks = {}, sof_authors = [] } = state;

  return (
    // Reverted to <View> so it doesn't force a new page, but kept wrap={true} so content can flow
    <View style={styles.page} wrap={true}>

      {pdfSaveId && <Text style={styles.pdfSaveId} wrap={false}>ID: {pdfSaveId}</Text>}

      <Text
        style={[
          styles.header,
          {
            textAlign: "center",
            borderBottomWidth: 1,
            borderColor: "#123460",
            marginBottom: 10,
            fontSize: 10,
            fontWeight: "bold",
            color: "#123460",
          },
        ]}
      >
        Statement of Facts for{" "}
        <Text style={{ fontWeight: "bold" }}>{vesselData.vesselName}</Text> B/L
        Dated{" "}
        <Text style={{ fontWeight: "bold" }}>
          {vesselData.billOfLadingDate
            ? moment(vesselData.billOfLadingDate).format("DD-MMM-YYYY")
            : "N/A"}
        </Text>
        : {vesselData.shipmentId || "N/A"} For{" "}
        <Text style={{ fontWeight: "bold" }}>{vesselData.port || "N/A"}</Text> -{" "}
        {vesselData.operation || "N/A"}
      </Text>

      {/* --- Vessel Details (2-Column KV) --- */}
      <SectionTitle title="Vessel Particulars" />
      <View style={styles.kvContainer} wrap={true}>
        {Object.entries(vesselData).map(([key, value], index) => (
          <View key={index} style={styles.kvItem} wrap={false}>
            <Text style={styles.kvLabel}>{key.replace(/([A-Z])/g, ' $1').toUpperCase()}</Text>
            <Text style={styles.kvValue}>{value || "-"}</Text>
          </View>
        ))}
      </View>

      {/* --- Additional Data (2-Column KV) --- */}
      {userFields.length > 0 && (
        <>
          <SectionTitle title="Additional Data" />
          <View style={styles.kvContainer} wrap={true}>
            {userFields.map((field, index) => (
              <View key={index} style={styles.kvItem} wrap={false}>
                <Text style={styles.kvLabel}>{field.label || "FIELD"}</Text>
                <Text style={styles.kvValue}>{field.value || "-"}</Text>
              </View>
            ))}
          </View>
        </>
      )}

      {/* --- Events Table --- */}
      <SectionTitle title="Events Log" />
      <View style={styles.table} wrap={true}>
        <View style={styles.tableHeader} wrap={false}>
          <Text style={[styles.tableHeaderCell, { width: "15%" }]}>Day</Text>
          <Text style={[styles.tableHeaderCell, { width: "25%" }]}>Date & Time</Text>
          <Text style={[styles.tableHeaderCell, { width: "60%", borderRightWidth: 0 }]}>Description</Text>
        </View>
        {events.map((event, index) => (
          <View key={index} style={[styles.tableRow, index % 2 !== 0 ? { backgroundColor: "#FAFBFC" } : {}]} wrap={false}>
            <Text style={[styles.tableCell, { width: "15%" }]}>{event.day || "-"}</Text>
            <Text style={[styles.tableCell, { width: "25%" }]}>
              {event.time ? moment(event.time).format("DD-MMM-YYYY HH:mm") : "-"}
            </Text>
            <Text style={[styles.tableCell, { width: "60%", borderRightWidth: 0 }]}>{event.event || "-"}</Text>
          </View>
        ))}
      </View>

      {/* --- Remarks (Vertical Layout, natural wrapping) --- */}
      {Object.keys(remarks).length > 0 && (
        <View wrap={true}>
          <SectionTitle title="Remarks" />
          {Object.entries(remarks)
            .filter(([type]) => type !== "_id")
            .map(([type, lines]) => (
              <View key={type} style={styles.remarkBlock} wrap={false}>
                <Text style={styles.remarkHeader}>{type.toUpperCase()} REMARKS</Text>
                <View style={styles.remarkBody}>
                  {lines.map((text, idx) => (
                    <Text key={idx} style={styles.remarkLine}>• {text}</Text>
                  ))}
                </View>
              </View>
            ))}
        </View>
      )}

      {/* --- Signatures --- */}
      {sof_authors.length > 0 && (
        <View wrap={false}>
          <SectionTitle title="Signatures" />
          <View style={styles.authorGrid}>
            {sof_authors.map((person) => (
              <View key={person.id} style={styles.authorCard}>
                <Text style={{ fontFamily: "Helvetica-Bold", fontSize: 8 }}>{person.name}</Text>
                <Text style={{ color: colors.textLabel, fontSize: 7, marginTop: 2 }}>{person.designation}</Text>
                <Text style={{ color: colors.textLabel, fontSize: 6, marginTop: 4 }}>{person.details}</Text>
              </View>
            ))}
          </View>
        </View>
      )}
    </View>
  );
};

export default StatementOfFactsPdf;